import { MY_TASKS_SOURCE } from "@jugl-web/domain-resources/tasks";
import { TaskCard } from "@jugl-web/domain-resources/tasks/components/TaskCard";
import { CustomerTaskCardProvider } from "@jugl-web/domain-resources/tasks/providers/TaskCardProvider";
import { CRMChannel, useRestApiProvider } from "@jugl-web/rest-api";
import { CustomerTaskModel } from "@jugl-web/rest-api/tasks/models/CustomerTask";
import {
  DataLoadingWrapper,
  Menu,
  PlainButton,
  SidebarDrawer,
  inputValueToCustomerPhoneString,
  phoneStringToInputValue,
} from "@jugl-web/ui-components";
import { cx, useTranslations } from "@jugl-web/utils";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { VList, VListHandle } from "virtua";
import { whatsappConversations$ } from "@jugl-web/rest-api/integrations/hooks/useWhatsappConversations";
import { instagramConversations$ } from "@jugl-web/rest-api/integrations/hooks/useInstagramConversations";
import { webChatBotConversations$ } from "@jugl-web/rest-api/integrations/hooks/useWebChatBotConversations";
import { twilioConversations$ } from "@jugl-web/rest-api/integrations/hooks/useTwilioConversations";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { formatWhatsappPhoneNumber } from "../../utils/formatWhatsappPhoneNumber";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as BlockIcon } from "./assets/block.svg";
import { ReactComponent as MoreIcon } from "./assets/more.svg";
import { ReactComponent as UnblockIcon } from "./assets/unblock.svg";
import { ReactComponent as WhatsappIcon } from "./assets/whatsapp.svg";
import { ManageContactAlert } from "./components/ManageContactAlert";
import { useWhatsappAccount } from "../../../../providers/WhatsappAccountProvider/useWhatsappAccount";

type PageState = {
  pageNumber: number;
  requestId: number;
  isError?: boolean;
  hasMore?: boolean;
};

export const ConversationInfoSidebar: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  isChatExpired?: boolean;
  isContactBlocked?: boolean;
}> = ({ isOpen, onClose, isContactBlocked, isChatExpired }) => {
  const [isManageAccountAlertOpen, setIsManageAccountAlertOpen] =
    useState(false);
  const { t } = useTranslations();
  const { integrationsApi, tasksApi } = useRestApiProvider();
  const { entityId } = useEntitySelectedProvider();
  const { navigateToPage } = useNavigation();
  const {
    selectedConversation,
    refetchTrigger$,
    selectConversation,
    selectedChannel,
  } = useClientSupportPageContext();
  const { whatsappAgent } = useWhatsappAccount();
  const currentPageState = useRef<PageState | null>(null);
  const [customerTasks, setCustomerTasks] = useState<CustomerTaskModel[]>([]);
  const vListRef = useRef<VListHandle>(null);

  const [
    getCustomerTasks,
    {
      isLoading: isCustomerTasksLoading,
      isFetching: isCustomerTasksFetching,
      isError: isCustomerTasksError,
    },
  ] = tasksApi.useLazyGetCustomerTasksQuery();

  const [manageContact, { isLoading: isManageContactLoading }] =
    integrationsApi.useManageContactMutation();

  const formattedPhoneNumber = selectedConversation
    ? formatWhatsappPhoneNumber(
        selectedConversation.conv_id,
        whatsappAgent?.settings.hide_num || false
      )
    : "";

  const handleManageContact = useCallback(async () => {
    if (!selectedConversation) return;

    const response = await manageContact({
      entityId,
      userId: selectedConversation.conv_id,
      type: isContactBlocked ? "unblock" : "block",
    });

    if (response && "data" in response) {
      const conversationId = selectedConversation.conv_id;

      switch (selectedChannel) {
        case CRMChannel.whatsapp:
          whatsappConversations$.next({ type: "remove", conversationId });
          break;
        case CRMChannel.instagram:
          instagramConversations$.next({ type: "remove", conversationId });
          break;
        case CRMChannel.chatbot:
          webChatBotConversations$.next({ type: "remove", conversationId });
          break;
        case CRMChannel.twilio:
          twilioConversations$.next({ type: "remove", conversationId });
          break;
        default:
          break;
      }

      refetchTrigger$.next();
      selectConversation(undefined);
    }
  }, [
    selectedConversation,
    refetchTrigger$,
    manageContact,
    entityId,
    isContactBlocked,
    selectConversation,
    selectedChannel,
  ]);

  const loadNextPage = useCallback(async () => {
    if (!selectedConversation?.conv_id) return;

    let nextPageNumber = 1;
    if (currentPageState.current) {
      if (currentPageState.current.isError) {
        nextPageNumber = currentPageState.current.pageNumber;
      } else {
        nextPageNumber = currentPageState.current.pageNumber + 1;
      }
    }

    const nextPageState: PageState = {
      requestId: Math.random(),
      pageNumber: nextPageNumber,
    };

    currentPageState.current = nextPageState;

    const result = await getCustomerTasks({
      entityId,
      params: {
        phone_number: inputValueToCustomerPhoneString(
          phoneStringToInputValue(`+${selectedConversation.conv_id}`)
        ),
        page: nextPageNumber,
        page_size: 10,
      },
    });

    if (currentPageState.current?.requestId !== nextPageState.requestId) {
      return;
    }

    if ("error" in result && result.error) {
      nextPageState.isError = true;
      currentPageState.current = nextPageState;
      return;
    }

    if ("data" in result && result.data && result.data.data) {
      const responseData = result.data.data;

      setCustomerTasks((prev) =>
        nextPageNumber === 1 ? responseData : [...prev, ...responseData]
      );

      nextPageState.hasMore = result.data.total_pages > nextPageNumber;
      currentPageState.current = nextPageState;
    }
  }, [entityId, selectedConversation?.conv_id, getCustomerTasks]);

  useEffect(() => {
    if (selectedConversation?.conv_id && isOpen) {
      currentPageState.current = null;
      loadNextPage();
    }
  }, [selectedConversation?.conv_id, loadNextPage, isOpen]);

  const handleVListOnScroll = useCallback(
    (offset: number) => {
      if (!vListRef.current) {
        return;
      }
      if (!currentPageState.current?.hasMore || isCustomerTasksLoading) {
        return;
      }
      const bottomOffset =
        offset - vListRef.current.scrollSize + vListRef.current.viewportSize;

      if (bottomOffset > -150) {
        loadNextPage();
      }
    },
    [loadNextPage, isCustomerTasksLoading]
  );

  if (!selectedConversation) return null;
  return (
    <>
      <SidebarDrawer
        className="w-[400px]"
        isOpen={isOpen}
        onClose={onClose}
        customHeader={
          <div className="flex h-[63px] w-full shrink-0 items-center gap-2 px-8">
            <PlainButton onClick={onClose}>
              <ArrowIcon />
            </PlainButton>
            <span className="font-secondary leading-2 text-[15px] font-semibold text-[#333333] drop-shadow-[-2px_0_3px_rgba(0,0,0,0.05)]">
              {t({
                id: "client-support-page.chat-info",
                defaultMessage: "Chat Info",
              })}
            </span>
          </div>
        }
      >
        <div className="flex shrink-0 items-center justify-between gap-2 truncate bg-[linear-gradient(180deg,_#DEDEE5_0%,_#F3F3F3_58.19%)] p-8">
          <div className="flex items-center gap-[14px] truncate">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border border-solid border-white bg-[#D7F9DA]">
              <WhatsappIcon />
            </div>
            <div className="font-secondary flex flex-col gap-1 truncate">
              <span className="truncate text-lg font-medium leading-[27px] text-[#1A2023]">
                {selectedConversation.name}
              </span>
              <span className="text-grey-900 truncate text-sm leading-[140%]">
                {formattedPhoneNumber}
              </span>
              {isContactBlocked && (
                <span className="text-tertiary-800 text-sm leading-[140%]">
                  {t({
                    id: "client-support-page.blocked-account",
                    defaultMessage: "Blocked Account",
                  })}
                </span>
              )}
            </div>
          </div>
          {!isManageContactLoading && (!isChatExpired || isContactBlocked) && (
            <Menu
              placement="bottom-end"
              autoClose
              onSelect={() => setIsManageAccountAlertOpen(true)}
              sections={[
                [
                  {
                    id: "block-user",
                    icon: <BlockIcon />,
                    label: t({
                      id: "client-support-page.block-user",
                      defaultMessage: "Block User",
                    }),
                    isHidden: isContactBlocked,
                  },
                  {
                    id: "unblock-user",
                    icon: <UnblockIcon />,
                    label: t({
                      id: "client-support-page.unblock-user",
                      defaultMessage: "Unblock User",
                    }),
                    isHidden: !isContactBlocked,
                  },
                ],
              ]}
              renderTrigger={({ Trigger, triggerRef }) => (
                <Trigger
                  ref={triggerRef}
                  as={PlainButton}
                  className="hover:bg-dark/5 flex h-8 w-8 items-center justify-center rounded-full transition-colors"
                >
                  <MoreIcon />
                </Trigger>
              )}
            />
          )}
        </div>
        <DataLoadingWrapper
          isLoading={
            isCustomerTasksLoading ||
            (isCustomerTasksFetching &&
              currentPageState.current?.pageNumber === 1)
          }
          isFetching={isCustomerTasksFetching}
          isError={isCustomerTasksError}
          onRetry={loadNextPage}
        >
          {customerTasks.length ? (
            <VList
              overscan={10}
              onScroll={handleVListOnScroll}
              ref={vListRef}
              className="jugl__custom-scrollbar my-[34px] px-6"
            >
              {customerTasks.map((customerTask, idx) => (
                <CustomerTaskCardProvider
                  key={customerTask.id}
                  entityId={entityId}
                  source={MY_TASKS_SOURCE}
                  task={customerTask}
                >
                  {(props) => (
                    <TaskCard
                      onClick={() =>
                        navigateToPage("taskDetails", {
                          taskId: customerTask.id,
                        })
                      }
                      onTaskPreview={() =>
                        navigateToPage("taskDetails", {
                          taskId: customerTask.id,
                        })
                      }
                      className={cx("shrink-0", idx !== 0 && "mt-4")}
                      {...props}
                    />
                  )}
                </CustomerTaskCardProvider>
              ))}
            </VList>
          ) : (
            <span className="font-secondary px-6 py-[34px] text-center text-[13px] leading-[150%] text-[#828282]">
              {t({
                id: "client-support-page.no-tasks-related-to-customer",
                defaultMessage: "No Tasks related to this Customer",
              })}
            </span>
          )}
        </DataLoadingWrapper>
      </SidebarDrawer>
      <ManageContactAlert
        isOpen={isManageAccountAlertOpen}
        onConfirm={() => {
          handleManageContact();
          setIsManageAccountAlertOpen(false);
        }}
        onRequestClose={() => setIsManageAccountAlertOpen(false)}
        type={isContactBlocked ? "unblock" : "block"}
        phoneNumber={formattedPhoneNumber}
      />
    </>
  );
};
